/**
 * TileCTA.tsx — Tile call-to-action button
 *
 * Cycle 5 change: 'launch' action now triggers POST /api/broker/v1/bindings
 * via the useBinding hook instead of directly navigating to tile.href.
 * tile.href is retained in TileConfig for non-vertical-ref modes (ADR-046).
 *
 * catalogue-card-v2: pill sizing only (`.dp-btn` now renders as a pill via
 * global.css) — no `width: 100%`, all four behavior paths unchanged.
 *
 * Design-contract §5.1 (Tile primitive), §11 (Binding Lifecycle)
 */

import { t } from '../i18n';
import type { TileConfig } from '../config/schema';
import { useBinding } from '../hooks/useBinding';
import { useAuth } from '../auth/KeycloakProvider';

interface TileCTAProps {
  tile: TileConfig;
  onOrder?: () => void;
  /** When true, use binding flow instead of tile.href for launch action */
  useBindingFlow?: boolean;
}

export default function TileCTA({ tile, onOrder, useBindingFlow = false }: TileCTAProps) {
  const { token } = useAuth();
  const { state: bindingState, trigger } = useBinding(tile.id, token);

  if (tile.action === 'launch') {
    if (useBindingFlow) {
      // Cycle 5 path: POST /bindings → redirect_uri
      const isLoading = bindingState === 'submitting';
      return (
        <button
          className="dp-btn dp-btn--secondary"
          style={{ opacity: isLoading ? 0.7 : 1 }}
          disabled={isLoading}
          onClick={(e) => { e.stopPropagation(); void trigger(); }}
        >
          {isLoading ? t('tile.cta.launching') : t('tile.cta.launch')}
          {!isLoading && <span aria-hidden="true" style={{ marginLeft: 'var(--sp-1)' }}>↗</span>}
        </button>
      );
    }
    // Legacy path for non-vertical-ref modes (tier-0, reference, tier-1-hub)
    return (
      <a
        href={tile.href ?? '#'}
        target="_blank"
        rel="noopener noreferrer"
        className="dp-btn dp-btn--secondary"
        onClick={(e) => e.stopPropagation()}
      >
        {t('tile.cta.launch')}
        <span aria-hidden="true" style={{ marginLeft: 'var(--sp-1)' }}>↗</span>
      </a>
    );
  }

  if (tile.action === 'order') {
    return (
      <button
        className="dp-btn dp-btn--primary"
        onClick={(e) => { e.stopPropagation(); onOrder?.(); }}
      >
        {t('tile.cta.order')}
      </button>
    );
  }

  return (
    <button
      className="dp-btn dp-btn--ghost"
      onClick={(e) => e.stopPropagation()}
    >
      {t('tile.cta.manage')}
    </button>
  );
}
